import { Group } from '@visx/group'
import { withParentSize } from '@visx/responsive'
import { scaleBand, scaleLinear } from '@visx/scale'
import { ScaleBand, ScaleLinear } from '@visx/vendor/d3-scale'
import { UsageCounterPeriod } from 'models'
import { useMemo } from 'react'

const DEFAULT_MARGIN = { top: 0, right: 0, bottom: 0, left: 0 }

export function SimpleBarChart({
  parentWidth,
  parentHeight,
  margin = DEFAULT_MARGIN,
  background = 'white',
  xDomain,
  yDomain,
  children,
  svgClassName,
}: {
  parentWidth: number
  parentHeight: number
} & {
  margin?: { top: number; right: number; bottom: number; left: number }
  timezone?: string
  background?: string
  xDomain: number[]
  yDomain: number[]
  values?: Array<any>
  granularity?: UsageCounterPeriod
  children?: ({
    xScale,
    yScale,
    xMax,
    yMax,
  }: {
    xScale: ScaleBand<number>
    yScale: ScaleLinear<number, number, never>
    xMax: number
    yMax: number
    parentWidth: number
  }) => React.ReactElement | null
  svgClassName?: string
  handlePointer?: (x: number | null, tooltipLeft: number | null) => number | null
}) {
  const width = parentWidth
  const height = parentHeight

  // bounds
  const xMax = width - margin.left - margin.right
  const yMax = height - margin.top - margin.bottom

  // scales
  const xScale = useMemo(
    () =>
      scaleBand<number>({
        domain: xDomain,
        range: [0, xMax],
        padding: 0.2,
      }),
    [xDomain, xMax]
  )
  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: yDomain,
        range: [yMax, 0],
        nice: true,
      }),
    [yDomain, yMax]
  )

  if (width < 10) {
    return null
  }

  return (
    <svg width={width} height={height} className={svgClassName}>
      <rect x={0} y={0} width={width} height={height} fill={background} />
      <Group left={margin.left} top={margin.top}>
        <ClipPath id='content' width={xMax} height={yMax} />
        {children?.({ xScale, yScale, xMax, yMax, parentWidth })}
      </Group>
    </svg>
  )
}

function ClipPath({ id, width, height }: { id: string; width: number; height: number }) {
  return (
    <defs>
      <clipPath id={id}>
        <rect width={width} height={height} />
      </clipPath>
    </defs>
  )
}

export const ResponsiveSimpleBarChart = withParentSize(SimpleBarChart)
